<template>
  <v-dialog
    :value="value"
    :persistent="isResetPasswordView || loading.updatePassword"
    width="400"
    @input="$emit('input', $event)"
  >
    <v-card :disabled="loading.updatePassword" :loading="loading.updatePassword">
      <v-form
        v-model="validForm"
        :disabled="loading.updatePassword"
        @submit.prevent="submitChanges"
      >
        <v-card-title v-if="isResetPasswordView">Reset account password</v-card-title>
        <v-card-title v-else>Change account password</v-card-title>

        <v-card-text>
          <!-- Used for correct autocomplete -->
          <input v-show="false">
          <input
            v-if="isResetPasswordView"
            type="password"
            style="display: block; width: 0; height: 0;"
          >

          <v-text-field
            v-if="!isResetPasswordView"
            v-model="formData.password"
            :type="show.password ? 'text' : 'password'"
            :rules="rules.password"
            :append-icon="show.password ? 'visibility' : 'visibility_off'"
            label="Current password"
            outlined
            hide-details
            @click:append="show.password = !show.password"
          />

          <div
            :class="{ 'blue-grey lighten-4 px-2 py-3': isResetPasswordView }"
            class="rounded mb-6"
          >
            <v-text-field
              v-if="isResetPasswordView"
              v-model="formData.resetCode"
              :rules="rules.resetCode"
              label="Reset code"
              outlined
              background-color="white"
              hide-details
              autocomplete="off"
            />
            <div class="d-flex justify-space-between align-center mt-2">
              <div v-if="isResetPasswordView">
                Reset code was sent to your account email address
              </div>
              <div v-else>
                You can use reset code instead
              </div>
              <v-btn
                :disabled="!!sendCodeUnavailableUntil && !isResetCodeSent"
                :loading="loading.sendResetCode"
                :color="isResetCodeSent ? 'green' : 'blue'"
                small
                min-width="70"
                outlined
                class="ml-2"
                @click="sendResetCode"
              >
                <v-icon v-if="isResetCodeSent" left>done</v-icon>
                <span>{{ sendResetCodeText }}</span>
              </v-btn>
            </div>
          </div>

          <v-text-field
            v-model="formData.newPassword"
            :type="show.newPassword ? 'text' : 'password'"
            :rules="rules.password"
            :append-icon="show.newPassword ? 'visibility' : 'visibility_off'"
            label="New password"
            outlined
            @click:append="show.newPassword = !show.newPassword"
          />
          <v-text-field
            v-model="formData.confirmNewPassword"
            :rules="rules.password"
            type="password"
            label="Confirm new password"
            outlined
          />
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-btn
            v-if="isResetPasswordView"
            text
            @click="cancelReset"
          >
            Cancel reset
          </v-btn>
          <v-btn
            v-else
            text
            @click="close"
          >
            Close
          </v-btn>
          <v-spacer />
          <v-btn
            :disabled="!validForm"
            type="submit"
            color="blue"
            class="white--text"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import { useIntervalTimer } from '@/helpers/timers';
import { passwordRegex } from '@/helpers/regex';
import { handleReqErrorWithToast } from '@/helpers/error-handlers';

function getInitialFormData() {
  return {
    password: '',
    resetCode: '',
    newPassword: '',
    confirmNewPassword: '',
  };
}

export default {
  name: 'EditChildPasswordDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isResetPasswordView: false,
      isResetCodeSent: false,
      sendCodeUnavailableUntil: null,
      sendCodeUnavailableTime: null,
      validForm: false,
      formData: getInitialFormData(),
      rules: {
        password: [
          v => !!v || 'Field is required',
          v => v.length >= 8 || 'Min length: 8',
          v => passwordRegex.test(v)
            || 'Password must be at least 8 characters long and contain at least one lowercase letter, uppercase letter, number and symbol',
        ],
        resetCode: [
          v => !!v || 'Field is required',
        ],
      },
      loading: {
        sendResetCode: false,
        updatePassword: false,
      },
      show: {
        password: false,
        newPassword: false,
      },
    };
  },
  computed: {
    ...mapGetters('children', [
      'activeChildProfile',
    ]),
    formDataPayload() {
      if (this.isResetPasswordView) {
        return { resetCode: this.formData.resetCode, newPassword: this.formData.newPassword };
      }
      return { password: this.formData.password, newPassword: this.formData.newPassword };
    },
    sendResetCodeText() {
      if (this.isResetCodeSent) {
        return 'Code sent';
      }
      if (this.sendCodeUnavailableUntil) {
        return this.sendCodeUnavailableTime;
      }
      return this.isResetPasswordView ? 'Resend code' : 'Send code';
    },
  },
  methods: {
    ...mapActions('children', [
      'sendResetPasswordCode',
      'updateChildPassword',
    ]),
    submitChanges() {
      if (!this.validForm || this.loading.updatePassword) return;

      this.loading.updatePassword = true;

      const payload = cloneDeep(this.formDataPayload);
      this.updateChildPassword(payload)
        .then(() => {
          this.$toast.success('Account password successfully updated');
          this.setInitialData();
          this.close();
        })
        .catch(err => handleReqErrorWithToast(err, 'Failed to update password'))
        .finally(() => (this.loading.updatePassword = false));
    },
    sendResetCode() {
      if (this.isResetCodeSent) {
        this.isResetCodeSent = false;
        return;
      }
      if (this.sendCodeUnavailableUntil || this.loading.sendResetCode) return;

      this.loading.sendResetCode = true;

      this.sendResetPasswordCode()
        .then(() => {
          this.$toast.success('Password reset code sent to your email');
          this.isResetPasswordView = true;
          this.isResetCodeSent = true;
          this.setSendCodeTimer();
        })
        .catch(err => handleReqErrorWithToast(err, 'Failed to send password reset code'))
        .finally(() => (this.loading.sendResetCode = false));
    },
    setSendCodeTimer() {
      useIntervalTimer('1m', (secondsLeft, timerUntil) => {
        this.sendCodeUnavailableTime = secondsLeft;
        this.sendCodeUnavailableUntil = timerUntil;
        if (!timerUntil) {
          this.isResetCodeSent = false;
        }
      });
    },
    cancelReset() {
      this.formData.resetCode = '';
      this.isResetPasswordView = false;
      this.isResetCodeSent = false;
    },
    setInitialData() {
      this.formData = getInitialFormData();
      this.isResetPasswordView = false;
      this.show.password = false;
      this.show.newPassword = false;
    },
    close() {
      this.$emit('input', false);
    },
  },
};
</script>
